<template>
  <b-navbar class="main-navbar" :class="{ 'main-navbar-unlogged': !isLoggedView }" shadow>
    <template #brand>
      <b-navbar-item v-if="!isLoggedView" tag="div">
        <img src="@/assets/images/rf.png" alt="République Française" />
      </b-navbar-item>
      <b-navbar-item
        href="https://ofb.gouv.fr/"
        target="_blank"
        title="Office Français de la Biodiversité"
      >
        <img src="@/assets/images/ofb.png" alt="Office Français de la Biodiversité" />
      </b-navbar-item>
      <b-navbar-item tag="router-link" :to="{ path: '/' }" :title="$t('menu.home')">
        <img src="@/assets/images/ae.png" alt="SAGAE" />
      </b-navbar-item>
    </template>

    <template #start>
      <div class="MenuView-titles">
        <div :class="`MenuView-main-title ${!subTitle ? 'big' : ''}`">
          <span v-if="!displaySubTitleInGreen">{{ mainTitle }}</span>
          <span v-else>{{ subTitle }}</span>
        </div>
        <div v-if="subTitle" class="MenuView-subtitle">
          <img class="menu-icon left" src="/img/poisson.svg" alt="Poisson" />
          <span v-if="!subTitleIsEditable" class="non-editable">
            <span v-if="!displaySubTitleInGreen">
              {{ subTitle }}
            </span>
            <span v-else>
              {{ mainTitle }}
            </span>
          </span>
          <span v-else-if="subTitleIsEditable && !edit" class="editable" @click="enterEdition"
            >{{ subTitle }}&nbsp;
            <b-icon icon="edit" size="is-small" style="cursor: pointer"></b-icon
          ></span>
          <div v-else class="MenuView-subtitle-edited">
            <InputWithValidation
              ref="validationInput"
              v-model="editedSubTitle"
              rules="required|min:3|max:140"
              type="text"
              :hidden-label="true"
              :placeholder="$t('input-name-tooltip')"
              :maxlength="140"
              :control-action="true"
              :has-counter="false"
              @update:model-value="onInputChange"
              @validation-changed="onValidationChanged"
            >
            </InputWithValidation>
            <div class="MenuView-subtitle-edited-buttons">
              <b-button @click="edit = false">{{ $t("annuler") }}</b-button>
              <b-button type="is-primary" :disabled="!isFormValid || !hasChanged" @click="submit">
                {{ $t("valider") }}
              </b-button>
            </div>
          </div>
          <img v-if="subTitle" class="menu-icon right" src="/img/scarabee.svg" alt="Scarabée" />
        </div>
      </div>
    </template>

    <template #end>
      <div class="buttons">
        <router-link v-show="!isLoggedView" to="/register/user/0" class="button is-primary">
          <strong> {{ $t("register") }}</strong>
        </router-link>

        <b-tooltip v-if="isLoggedView" :label="$t('logout')" position="is-left" class="mr-2">
          <b-button
            rounded
            outlined
            icon-left="power-off"
            type="is-danger"
            class="logout"
            @click="logout()"
          ></b-button>
        </b-tooltip>

        <div v-if="isLoggedView" class="profile-circle" role="button" @click="emitOpenProfile()">
          <span>{{ getInitials(loggedUser) }} </span>
        </div>

        <b-dropdown
          v-if="!isLoggedView"
          ref="login-dropdown"
          position="is-bottom-left"
          append-to-body
          aria-role="menu"
          trap-focus
          style="height: 100%"
        >
          <template #trigger>
            <a v-if="$route.name != 'Login'" class="navbar-item" role="button">
              <span>{{ $t("login") }}</span>
              <b-icon icon="angle-down"></b-icon>
            </a>
          </template>

          <b-dropdown-item aria-role="menu-item" :focusable="false" custom paddingless>
            <LoginView :embeded="true" @hide-drop-down="hideDropDown" />
          </b-dropdown-item>
        </b-dropdown>
      </div>
    </template>
  </b-navbar>
</template>

<script lang="ts">
import { BNavbar, BNavbarItem, BButton, BDropdown, BDropdownItem, BIcon, BTooltip } from "buefy"

import { defineComponent } from "vue"
import LoginView from "@/views/login/LoginView.vue"
import Activable from "../utils/Activable"
import { UtilisateurDTO } from "@/model/bean/GeneratedDTOs"
import InputWithValidation from "../inputs/InputWithValidation.vue"
import PageChangeForbidder from "@/router/PageChangeForbidder"
import { eventBus } from "@/services/EventBus"

export default defineComponent({
  components: {
    LoginView,
    InputWithValidation,
    BNavbar,
    BNavbarItem,
    BButton,
    BDropdown,
    BDropdownItem,
    BIcon,
    BTooltip,
  },
  props: {
    loggedUser: UtilisateurDTO,
    mainTitle: { type: String, default: "" },
    subTitle: { type: String, default: "" },
    subTitleIcon: { type: String, default: "" },
    subTitleIsEditable: { type: Boolean, default: false },
    displaySubTitleInGreen: { type: Boolean, default: false },
    isLoggedView: { type: Boolean, default: false, required: true },
  },
  emits: ["log-out", "open-profile", "sub-title-edited"],
  data() {
    return {
      edit: false,
      editedSubTitle: "",
      originalSubTitle: "", // Stocker la valeur initiale
      isFormValid: false,
      hasChanged: false, // Vérifier si le texte a été modifié
    }
  },
  watch: {
    subTitle(): void {
      this.edit = false
      this.editedSubTitle = this.subTitle || ""
      this.originalSubTitle = this.subTitle || ""
      this.hasChanged = false
      this.checkValidation()
    },
  },
  created() {
    this.editedSubTitle = this.subTitle || ""
    this.originalSubTitle = this.subTitle || ""
    this.hasChanged = false
    this.checkValidation()
  },
  methods: {
    logout(): void {
      if (!PageChangeForbidder.INSTANCE.pageChangeForbidden) {
        this.hideDropDown()
        this.$emit("log-out")
      } else {
        PageChangeForbidder.INSTANCE.callbackActionWhenForbidden(() => {
          PageChangeForbidder.INSTANCE.allowPageChange()
          this.hideDropDown()
          this.$emit("log-out")
        })
      }
    },

    emitOpenProfile(): void {
      this.hideDropDown()
      if (this.loggedUser) {
        this.$emit("open-profile", this.loggedUser.id)
      }
    },

    getInitials(loggedUser: UtilisateurDTO): string {
      let initials = ""
      if (loggedUser.prenom) {
        initials = loggedUser.prenom[0]
      }
      if (loggedUser.nom) {
        initials += loggedUser.nom[0]
      }

      return initials
    },

    hideDropDown(): void {
      // Hide dropdown if opened
      const loginDropdown = this.$refs["login-dropdown"] as unknown as Activable
      if (loginDropdown) {
        loginDropdown.isActive = false
      }
    },

    enterEdition(): void {
      this.editedSubTitle = this.subTitle
      this.originalSubTitle = this.subTitle || ""
      this.hasChanged = false
      this.$nextTick(() => {
        this.edit = true
        this.checkValidation()
      })
    },

    async onInputChange(): Promise<void> {
      // Vérifier si le texte a été modifié
      this.hasChanged = this.editedSubTitle !== this.originalSubTitle
      // Attendre le prochain tick pour que la validation soit mise à jour
      await this.$nextTick()
      this.checkValidation()
    },

    onValidationChanged(validationState: { valid: boolean; errors: string[] }): void {
      this.hasChanged = this.editedSubTitle !== this.originalSubTitle
      this.isFormValid = validationState.valid
    },

    checkValidation(): void {
      // Vérifier manuellement les règles de validation
      const value = this.editedSubTitle?.toString() || ""
      const isRequired = value.length > 0
      const isMinLength = value.length >= 3
      const isMaxLength = value.length <= 140

      this.isFormValid = isRequired && isMinLength && isMaxLength
    },

    submit(): void {
      this.edit = false
      eventBus.emit("sub-title-edited", this.editedSubTitle)
    },
  },
})
</script>

<style lang="scss">
@import "@/styles/variables";
// Imported to have access to responsive breakpoints
@import "../../../node_modules/bulma/sass/utilities/_all.sass";

$header-side-margin: 30px;

.main-navbar {
  height: $header-height;

  .navbar-item {
    flex: 1 1 auto;
    font-weight: bold;
    font-size: 18px;
    color: $text-default-color;
    height: 100%;
    padding-left: 10px;
    padding-right: 10px;
    justify-content: center;

    &.router-link-exact-active {
      color: $primary;
    }

    img {
      max-height: 60px;
    }
  }

  .navbar-brand {
    font-family: $font-family-title;
    font-size: 24px;
    padding-left: 0;
    padding-right: 0;
    height: $header-height;
    margin-left: $header-side-margin;
    margin-right: 10px;

    .navbar-item {
      padding-left: 0;
      padding-right: 0;
      flex: 1 0 auto;
    }
  }

  .navbar-menu {
    flex: 1 1 auto;
    justify-content: flex-end;

    div.navbar-start {
      margin: 0;
      flex: 1 1 auto;
    }
  }

  .navbar-end {
    margin-right: $header-side-margin;
  }

  .buttons:last-child,
  .buttons .button {
    margin-bottom: 0;
    margin-top: 0.5rem;
  }

  .navbar-burger {
    flex: 1 0 auto;
    height: 100%;
    margin-right: 50px;
  }
}

.main-navbar-unlogged {
  .navbar-start {
    display: none;
  }

  .navbar-item img {
    max-height: 150px;
  }

  &.main-navbar,
  .navbar-brand,
  .navbar-burger {
    height: $header-big-height;
  }
}

.MenuView-main-title {
  font-family: $font-family-title;
  font-weight: bold;
  color: $green-v2;
  font-size: 17px;
  text-decoration: underline;

  &.big {
    font-size: 24px;
    text-decoration: none;
  }
}

.MenuView-subtitle {
  font-size: 22px;
  margin-right: 80px;

  .menu-icon {
    margin-top: 3px;
    height: 25px;

    &.left {
      float: left;
      padding-right: 5px;
    }

    &.right {
      position: absolute;
      right: 170px;
      top: 38px;
    }
  }

  .editable {
    display: flex;
    align-items: center;

    .icon.is-small {
      height: 1rem !important;
      width: 1rem !important;
    }
  }

  .MenuView-subtitle-edited {
    display: flex;

    .MenuView-subtitle-edited-buttons {
      display: flex;

      .button {
        margin-left: 5px;
      }
    }
  }
}

.logout {
  opacity: 0;
  animation-duration: 1.5s;
  animation-delay: 1s;
  animation-name: logout_appear;
  animation-fill-mode: forwards;
}

.profile-circle {
  background-color: $green-v2;
  opacity: 0;
  height: 70px;
  width: 70px;
  border-radius: 20%;
  animation-duration: 1.5s;
  animation-delay: 0.5s;
  animation-name: menu_appear;
  animation-fill-mode: forwards;

  display: inline-flex;
  position: relative;
  vertical-align: top;

  span {
    height: 100%;
    width: 100%;
    position: relative;
    cursor: pointer;

    display: flex;
    align-items: center;
    justify-content: center;

    font-size: 25px;
    color: white;
    text-transform: uppercase;
    font-weight: 700;
  }

  &:hover span {
    transition: background-color 200ms linear;
    border-radius: 50%;
    height: 70px;
    width: 70px;
    border: 2px solid $green-v2;
    color: $green-v2;
    background-color: white;
  }
}

@keyframes menu_appear {
  from {
    border-radius: 10%;
    opacity: 0;
  }
  70% {
    opacity: 1;
  }
  to {
    border-radius: 50%;
    opacity: 1;
  }
}

@keyframes logout_appear {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media screen and (min-width: $desktop) {
  .main-navbar {
    .navbar-menu {
      .navbar-start {
        height: 100%;
        align-items: center;
        flex: 1 0 auto;
      }

      .navbar-end {
        flex: 0 1 auto;
      }
    }
  }
}

@media screen and (min-width: $fullhd) {
  .main-navbar {
    .navbar-item {
      height: 100%;
    }

    .navbar-menu {
      flex-direction: row;

      .navbar-start {
        align-items: center;
      }

      .navbar-end {
        flex: 0 1 auto;
      }
    }

    .navbar-brand {
      margin-left: 50px;
    }
  }

  .non-editable {
    cursor: default;
  }

  .editable {
    cursor: text;
  }
}
</style>
